/**
 * Dubbelcheck vóór versturen (Joris, 3 nov 2026): "Laat bij genereren en versturen callsheet de PL
 * nog dubbel check doen" → gekozen: een checklist vóór versturen.
 *
 * Klikt de PL in "Crew informeren" op Versturen, dan komt eerst dit venster met de kern: datum,
 * locatie + adres, tijden, crew (functie, contactgegevens compleet?), ontvangers en wat ontbreekt of
 * vreemd is. De PL vinkt elk punt af en bevestigt; pas dan gaat het bestaande vangnet (5 s
 * "Annuleren" bij e-mail, zie CallsheetBijlagen.informeerCrew) lopen. PDF maken/downloaden heeft
 * deze check niet nodig.
 *
 * Regels:
 *  - Rustig (feedback_rustige_schermen): één venster, korte punten, waarschuwingen in grijs-oranje,
 *    één primaire knop "Klopt, versturen". Die blijft uit tot alles is afgevinkt.
 *  - Waarschuwingen (iets ontbreekt of is vreemd) vragen één expliciet vinkje "gezien".
 *  - Blokkades (zonder dit heeft versturen geen zin): geen datum, niemand die het bericht kan
 *    krijgen (geen crewlid met bekend e-mailadres; melding, crew-app en mail lopen allemaal via dat
 *    adres), ontvangers niet vast te stellen (ophalen mislukt: dan weet niemand wie het krijgt),
 *    of geen kanaal gekozen. Die kun je niet wegvinken — eerst oplossen.
 *  - Wie de PL bevestigt, krijgt het: vlak vóór het echte verzoek (na de 5 s) worden de ontvangers
 *    opnieuw opgehaald (zelfdeOntvangers + informeerCrew.vooraf), én de bevestigde e-mailadressen
 *    gaan mee als verwachteOntvangers — de server verstuurt alleen bij exact dezelfde set (409).
 *  - Alleen wat het callsheet-scherm al weet; niets verzinnen. Datum = wat de server meestuurt
 *    ('Datum (handmatig)', anders 'Datum', eerste 10 tekens). Kent het scherm de pure StartDatum
 *    van de draaidag (develop), dan waarschuwt een afwijking — zo valt een UTC-lookup die in CEST
 *    een dag terugschuift op. StartdatumOutlook wordt nooit gebruikt.
 *
 * Bewust één bestand, gelijk op develop en main: de CallSheetManager verschilt per branch, deze
 * bouwsteen niet.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Typography,
} from '@mui/material';

const GRIJS = '#64748b';
const ORANJE = '#b45309';
const REC_ID = /^rec[A-Za-z0-9]{14}$/;
const GEEN_CONTACT = '-';   // bewust geen contact op deze callsheet (zie callsheetDocument op main)

const alsTekst = (v) => {
  const x = Array.isArray(v) ? v[0] : v;
  return x == null ? '' : String(x).trim();
};
/** "2026-11-10…" → "2026-11-10", alleen als het een echte kalenderdatum is (geen 2026-02-30). */
const isoDatum = (v) => {
  const s = alsTekst(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? s : '';
};
/** Seconden sinds middernacht (Calltijd/Tijd in Airtable) → "07:30". */
export const uurMin = (sec) => {
  if (sec == null || sec === '' || Number.isNaN(Number(sec))) return '';
  const n = Math.max(0, Math.round(Number(sec)));
  return `${String(Math.floor(n / 3600)).padStart(2, '0')}:${String(Math.floor((n % 3600) / 60)).padStart(2, '0')}`;
};
/** "8:00" / "08:00" → minuten; ongeldig → null. */
const minuten = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
export const vandaagIso = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const datumTekst = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
const telefoonVan = (c) => alsTekst(c.fields?.['Telefoon (handmatig)']) || alsTekst(c.fields?.['Telefoonnummer (from Personen)'])
  || alsTekst(c.fields?.Telefoonnummer);
/** Dezelfde naam die de server gebruikt om het e-mailadres te zoeken (crew-ontvangers). */
const serverNaam = (c) => alsTekst(c.fields?.['Volledige naam (from Personen)']) || alsTekst(c.fields?.Persoon);

/**
 * Callsheet-data (zoals CallSheetManager hem heeft) → de feiten voor de checklist.
 * `personen` is optioneel: daarmee worden oude crew-records met een record-ID als naam opgelost.
 */
export function feitenUitCallsheet({ fields = {}, draaidag = [], extra = {}, locaties = [], crew = [], personen = [] } = {}) {
  const df = (draaidag || [])[0]?.fields || {};
  const draaidagDatum = isoDatum(df.StartDatum);
  // Zelfde bron als de verzendroutes (frontdoor/Node): eerst de niet-lege handmatige waarde, anders
  // Datum, eerste 10 tekens. Is die waarde geen ISO-datum, dan géén terugval (de server stuurt
  // hem toch zo mee) maar een blokkade.
  const datumRuw = (alsTekst(fields['Datum (handmatig)']) || alsTekst(fields.Datum)).slice(0, 10);
  const datum = isoDatum(datumRuw);
  const persoonNaam = (id) => {
    const p = (personen || []).find((x) => x.id === id);
    return alsTekst(p?.fields?.['Volledige naam']) || alsTekst(p?.fields?.Naam);
  };
  const crewLijst = (crew || []).map((c) => {
    let naam = serverNaam(c);
    let naamOnbekend = false;
    if (REC_ID.test(naam)) {
      const opgelost = persoonNaam(naam);
      if (opgelost) naam = opgelost; else { naamOnbekend = true; }
    }
    return {
      id: c.id,
      naam: naamOnbekend ? '' : naam,
      serverNaam: serverNaam(c),
      personenId: alsTekst(c.fields?.Personen),
      naamOnbekend,
      functie: alsTekst(c.fields?.['Functie (handmatig)']),
      telefoon: telefoonVan(c),
      call: uurMin(c.fields?.Calltijd),
      callSec: c.fields?.Calltijd ?? null,
    };
  });
  const calls = crewLijst.map((c) => c.callSec).filter((s) => s != null && s !== '' && !Number.isNaN(Number(s)));
  const contactNaam = alsTekst(fields['Contactpersoon (handmatig)']) || alsTekst(fields.Contactpersoon);
  const contactTel = alsTekst(fields['Contactgegevens klant telefoon (Handmatig)']) || alsTekst(fields['Telefoonnummer klant']);
  return {
    datum,
    datumRuw,
    draaidagDatum,
    locaties: (locaties || []).map((l) => {
      const f = l.fields || {};
      return {
        id: l.id,
        naam: alsTekst(f['Naam locatie']),
        adres: [alsTekst(f.Straat), [alsTekst(f.Postcode), alsTekst(f.Plaats)].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      };
    }),
    call: calls.length ? uurMin(Math.min(...calls.map(Number))) : '',
    begin: alsTekst(extra.begintijd),
    eind: alsTekst(extra.eindtijd),
    crew: crewLijst,
    contactKlant: {
      naam: contactNaam === GEEN_CONTACT ? '' : contactNaam,
      telefoon: contactTel === GEEN_CONTACT ? '' : contactTel,
      bewustGeen: contactNaam === GEEN_CONTACT,
      telBewustGeen: contactTel === GEEN_CONTACT,
    },
    settelefoon: alsTekst(fields['FullFrame Settelefoonnummer']),
  };
}

const KANAAL_TEKST = { webapp: 'melding in intern', crewApp: 'FF Crew-app', mail: 'e-mail' };

/**
 * Feiten + ontvangers + kanalen → { waarschuwingen, blokkades }. Puur, zodat het te testen is.
 * ontvangers: null = nog bezig, { fout } = ophalen mislukt, { ontvangers: [{naam,email}], onbekend: [naam] }.
 */
export function bepaalVerzendCheck(feiten, { ontvangers = null, kanalen = {}, vandaag = vandaagIso() } = {}) {
  const w = [];
  const b = [];
  const f = feiten || {};
  const n = (k, een, meer) => `${k} ${k === 1 ? een : meer}`;

  if (!f.datum && f.datumRuw) b.push(`De datum op de callsheet ("${f.datumRuw}") is geen geldige datum`);
  else if (!f.datum) b.push('Geen datum op de callsheet');
  else {
    if (f.datum < vandaag) w.push(`De datum (${datumTekst(f.datum)}) ligt in het verleden`);
    if (f.draaidagDatum && f.datum !== f.draaidagDatum) {
      w.push(`Datum wijkt af van de draaidag (${datumTekst(f.draaidagDatum)})`);
    }
  }

  const locs = f.locaties || [];
  if (!locs.length) w.push('Geen locatie');
  else {
    const zonderAdres = locs.filter((l) => !l.adres).length;
    if (zonderAdres) w.push(`${n(zonderAdres, 'locatie', 'locaties')} zonder adres`);
  }

  if (!f.begin) w.push('Geen begintijd');
  if (!f.eind) w.push('Geen eindtijd');
  const mb = minuten(f.begin);
  const me = minuten(f.eind);
  if (mb != null && me != null && me <= mb) w.push(`Eindtijd (${f.eind}) ligt niet na de begintijd (${f.begin})`);
  const mc = minuten(f.call);
  if (mc != null && mb != null && mc > mb) w.push(`Eerste call (${f.call}) is later dan de begintijd (${f.begin})`);

  const crew = f.crew || [];
  const zonder = (pred) => crew.filter(pred).length;
  const onbekendeNaam = zonder((c) => c.naamOnbekend);
  if (onbekendeNaam) w.push(`${n(onbekendeNaam, 'crewlid', 'crewleden')} zonder herkenbare naam`);
  const zonderFunctie = zonder((c) => !c.functie);
  if (zonderFunctie) w.push(`${n(zonderFunctie, 'crewlid', 'crewleden')} zonder functie`);
  const zonderCall = zonder((c) => !c.call);
  if (zonderCall) w.push(`${n(zonderCall, 'crewlid', 'crewleden')} zonder calltijd`);
  const zonderTel = zonder((c) => !c.telefoon);
  if (zonderTel) w.push(`${n(zonderTel, 'crewlid', 'crewleden')} zonder telefoonnummer`);

  const ck = f.contactKlant || {};
  if (!ck.naam && !ck.bewustGeen) w.push('Geen contactpersoon klant');
  else if (!ck.telefoon && !ck.bewustGeen && !ck.telBewustGeen) w.push('Geen telefoonnummer contactpersoon klant');
  if (!f.settelefoon) w.push('Geen settelefoonnummer');

  if (!(kanalen.webapp || kanalen.crewApp || kanalen.mail)) b.push('Geen kanaal gekozen');
  if (ontvangers?.fout) {
    b.push('Ontvangers konden niet worden opgehaald — niet vast te stellen wie het krijgt');
  } else if (ontvangers) {
    const lijst = ontvangers.ontvangers || [];
    const onbekend = ontvangers.onbekend || [];
    if (!lijst.length) b.push('Niemand kan dit ontvangen: van geen enkel crewlid is een e-mailadres bekend');
    else if (onbekend.length) w.push(`Krijgt niets (geen e-mail bekend): ${onbekend.join(', ')}`);
  }
  return { waarschuwingen: w, blokkades: b };
}

/** Zelfde set e-mailadressen? (hoofdletters/volgorde maken niet uit) */
export function zelfdeOntvangers(a, b) {
  const set = (x) => [...new Set(((x?.ontvangers) || []).map((o) => String(o.email || '').trim().toLowerCase()).filter(Boolean))].sort();
  const sa = set(a);
  const sb = set(b);
  return sa.length === sb.length && sa.every((e, i) => e === sb[i]);
}

const PUNTEN = [
  { key: 'datum', label: 'Datum' },
  { key: 'locatie', label: 'Locatie' },
  { key: 'tijden', label: 'Tijden' },
  { key: 'crew', label: 'Crew' },
  { key: 'ontvangers', label: 'Ontvangers' },
];

function Regel({ children, zacht }) {
  return <Typography variant="body2" sx={{ color: zacht ? GRIJS : 'text.primary', lineHeight: 1.45 }}>{children}</Typography>;
}

function PuntInhoud({ k, feiten, ontvangers, kanalen, aantalBijlagen }) {
  const f = feiten || {};
  if (k === 'datum') return <Regel>{f.datum ? datumTekst(f.datum) : (f.datumRuw || '—')}</Regel>;
  if (k === 'locatie') {
    if (!(f.locaties || []).length) return <Regel zacht>—</Regel>;
    return (f.locaties || []).map((l, i) => (
      <Regel key={l.id || i}>{l.naam || 'Locatie'}{l.adres ? <Box component="span" sx={{ color: GRIJS }}> · {l.adres}</Box> : null}</Regel>
    ));
  }
  if (k === 'tijden') {
    const delen = [f.call && `call ${f.call}`, `begin ${f.begin || '?'}`, `eind ${f.eind || '?'}`].filter(Boolean);
    return <Regel>{delen.join(' · ')}</Regel>;
  }
  if (k === 'crew') {
    const zonderMail = new Set(ontvangers && !ontvangers.fout ? (ontvangers.onbekend || []) : []);
    return (f.crew || []).map((c) => {
      const mist = [!c.telefoon && 'geen telefoon', zonderMail.has(c.serverNaam) && 'geen e-mail'].filter(Boolean);
      return (
        <Regel key={c.id}>
          {c.naam || 'Onbekend crewlid'}
          <Box component="span" sx={{ color: GRIJS }}>{c.functie ? ` · ${c.functie}` : ''}{c.call ? ` · ${c.call}` : ''}</Box>
          {mist.length ? <Box component="span" sx={{ color: ORANJE }}> · {mist.join(', ')}</Box> : null}
        </Regel>
      );
    });
  }
  if (k === 'ontvangers') {
    const via = Object.keys(KANAAL_TEKST).filter((x) => kanalen?.[x]).map((x) => KANAAL_TEKST[x]);
    const bijlagen = kanalen?.mail && aantalBijlagen ? ` · ${aantalBijlagen} bijlage${aantalBijlagen === 1 ? '' : 'n'}` : '';
    if (!ontvangers) return <Regel zacht><CircularProgress size={12} sx={{ mr: 1 }} />Ontvangers ophalen…</Regel>;
    if (ontvangers.fout) return <Regel zacht>Niet op te halen · via {via.join(', ') || '—'}</Regel>;
    const lijst = ontvangers.ontvangers || [];
    return (
      <>
        <Regel>{lijst.length} {lijst.length === 1 ? 'persoon' : 'personen'}: {lijst.map((o) => o.naam || o.email).join(', ') || '—'}</Regel>
        <Regel zacht>via {via.join(', ') || '—'}{bijlagen}</Regel>
      </>
    );
  }
  return null;
}

/**
 * Het venster. `laadOntvangers` is een async functie → { ontvangers, onbekend } (read-only,
 * POST /Api/callsheets/:id/crew-ontvangers); wordt één keer per openen aangeroepen.
 */
export default function CallsheetVerzendCheck({ open, feiten, kanalen = {}, aantalBijlagen = 0, laadOntvangers, onTerug, onVerstuur, vandaag }) {
  const [ontvangers, setOntvangers] = useState(null);
  const [vinkjes, setVinkjes] = useState({});
  const [gezien, setGezien] = useState(false);
  const verstuurd = useRef(false);
  const laadRef = useRef(laadOntvangers);
  laadRef.current = laadOntvangers;

  // Opnieuw ophalen bij openen, bij een andere crew (verversen terwijl het venster open is) en
  // bij "Opnieuw proberen".
  const [poging, setPoging] = useState(0);
  const crewSleutel = JSON.stringify(((feiten || {}).crew || []).map((c) => [c.serverNaam, c.personenId]));
  useEffect(() => {
    if (!open) return undefined;
    let actief = true;
    verstuurd.current = false;
    setOntvangers(null);
    setVinkjes({});   // nieuwe ontvangers = opnieuw afvinken
    setGezien(false);
    Promise.resolve()
      .then(() => (laadRef.current ? laadRef.current() : Promise.reject(new Error('geen bron'))))
      .then((d) => { if (actief) setOntvangers({ ontvangers: d?.ontvangers || [], onbekend: d?.onbekend || [] }); })
      .catch(() => { if (actief) setOntvangers({ fout: true }); });
    return () => { actief = false; };
  }, [open, crewSleutel, poging]);

  const { waarschuwingen, blokkades } = useMemo(
    () => bepaalVerzendCheck(feiten, { ontvangers, kanalen, ...(vandaag ? { vandaag } : {}) }),
    [feiten, ontvangers, kanalen, vandaag],
  );
  // Veranderen de feiten terwijl het venster open is (verversen op de achtergrond), dan opnieuw
  // afvinken: een vinkje hoort bij wat de PL op dat moment zag. Tijdens het ophalen van de
  // ontvangers staan de vinkjes uit, zodat niemand afvinkt vóór alles er staat.
  const handtekening = JSON.stringify({ feiten, k: [kanalen.webapp, kanalen.crewApp, kanalen.mail], aantalBijlagen });
  useEffect(() => { setVinkjes({}); setGezien(false); }, [handtekening, open]);

  const allesAfgevinkt = PUNTEN.every((p) => vinkjes[p.key]);
  const bezig = !ontvangers;
  const kan = open && allesAfgevinkt && !bezig && !blokkades.length && (!waarschuwingen.length || gezien);

  const verstuur = () => {
    if (!kan || verstuurd.current) return;
    verstuurd.current = true;
    onVerstuur?.({ ontvangers });   // de bevestigde lijst; de manager controleert hem vlak vóór verzenden
  };

  return (
    <Dialog open={Boolean(open)} onClose={onTerug} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Klopt dit?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: GRIJS, mb: 1.5 }}>
          Loop de punten na en vink ze af. Pas daarna gaat het bericht naar de crew.
        </Typography>
        {PUNTEN.map((p) => (
          <Box key={p.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.75, borderTop: '1px solid #eef0f3' }}>
            <Checkbox size="small" checked={Boolean(vinkjes[p.key])} disabled={bezig}
              onChange={(e) => { if (!bezig) setVinkjes((v) => ({ ...v, [p.key]: e.target.checked })); }}
              inputProps={{ 'aria-label': `${p.label} klopt` }} sx={{ mt: -0.5 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: GRIJS, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>{p.label}</Typography>
              <PuntInhoud k={p.key} feiten={feiten} ontvangers={ontvangers} kanalen={kanalen} aantalBijlagen={aantalBijlagen} />
            </Box>
          </Box>
        ))}
        {blokkades.length > 0 && (
          <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }} data-testid="verzendcheck-blokkades">
            {blokkades.map((t) => <div key={t}>{t}</div>)}
            <Box sx={{ color: GRIJS, mt: 0.5 }}>Los dit eerst op; zo kan het niet weg.</Box>
            {ontvangers?.fout && (
              <Button size="small" onClick={() => setPoging((x) => x + 1)} sx={{ textTransform: 'none', mt: 0.5, px: 0 }}>Opnieuw proberen</Button>
            )}
          </Alert>
        )}
        {waarschuwingen.length > 0 && (
          <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: `3px solid #fcd34d` }} data-testid="verzendcheck-waarschuwingen">
            {waarschuwingen.map((t) => <Typography key={t} variant="body2" sx={{ color: ORANJE }}>{t}</Typography>)}
            <FormControlLabel sx={{ mt: 0.5 }}
              control={<Checkbox size="small" checked={gezien} disabled={bezig} onChange={(e) => { if (!bezig) setGezien(e.target.checked); }} />}
              label={<Typography variant="body2">Gezien, toch versturen</Typography>} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onTerug} sx={{ textTransform: 'none' }}>Terug</Button>
        <Button variant="contained" disableElevation onClick={verstuur} disabled={!kan} sx={{ textTransform: 'none' }}>
          Klopt, versturen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
